import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';

import { reportAxiosError } from '../Utils';

import useOrganization from './OrganizationContext';
import TextFieldFormik from './TextFieldFormik';

const ReopenClaimDialog = ({ classes, claim, open, onSubmit, onCancel }) => {
  const { reopenClaimOptions } = useOrganization();

  const handleReopenClaim = async (values) => {
    try {
      await axios.post(`/api/v1/claims/${claim.id}/reopen`, { note: values.note });
      return onSubmit();
    } catch (error) {
      reportAxiosError(error);
      throw error;
    }
  };

  const spacing = 1;
  return (
    <Formik
      initialValues={{ note: '' }}
      validationSchema={Yup.object().shape({
        note: Yup.string().required('Required'),
      })}
      enableReinitialize
      onSubmit={(values, formikProps) => {
        handleReopenClaim(values)
          .then(() => formikProps.resetForm())
          .catch(() => {
            formikProps.setSubmitting(false);
          });
      }}
    >
      {(formikProps) => {
        const { isSubmitting, handleReset, handleSubmit } = formikProps;

        return (
          <Dialog open={open} onClose={onCancel}>
            <DialogTitle>Reopen Claim</DialogTitle>
            <DialogContent>
              <Grid container spacing={spacing}>
                <Grid item xs={12}>
                  <Typography display="block" variant="body1">
                    Are you sure you want to reopen the claim?
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  {reopenClaimOptions.is_reason_free_text ? (
                    <TextFieldFormik id="note" label="Reason" multiline fullWidth className={classes.textField} />
                  ) : (
                    <TextFieldFormik id="note" label="Reason" className={classes.textField} select fullWidth>
                      {Object.entries(reopenClaimOptions.reason_options_map).map(([reason, { desc }]) => (
                        <MenuItem key={reason} value={reason}>
                          {desc}
                        </MenuItem>
                      ))}
                    </TextFieldFormik>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <CancelButton
                disabled={isSubmitting}
                onClick={() => {
                  handleReset();
                  onCancel();
                }}
                autoFocus
              />
              <Button disabled={isSubmitting} variant="contained" color="primary" onClick={handleSubmit}>
                Ok
              </Button>
            </DialogActions>
          </Dialog>
        );
      }}
    </Formik>
  );
};

ReopenClaimDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  claim: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ReopenClaimDialog;
